import {
  Alert,
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";

import { type ConfigMigrationStatusValue } from "./use-config-migration";

const styles = {
  root: { mt: 4, maxWidth: 720 },
  header: { mb: 1.5 },
  bundles: { mt: 1, mb: 2 },
  mono: { fontFamily: "monospace", fontSize: "0.8rem", wordBreak: "break-all" },
  reason: { display: "block" },
} satisfies Record<string, SxProps<Theme>>;

type ChipColor = "default" | "info" | "success" | "error";

type MatchChip = { color: "default" | "success" | "warning"; label: string };

/**
 * Picks the per-table outcome chip: a skipped/blocked table (with a reason)
 * reads as a neutral "skipped" rather than a red "mismatch", so the operator can
 * tell an intentional skip apart from unexpected row loss.
 *
 * @param matches Whether source and target counts matched.
 * @param skipped Whether the table was skipped or blocked for a stated reason.
 * @returns The chip color and label.
 */
const matchChip = (matches: boolean, skipped: boolean): MatchChip => {
  if (skipped) {
    return { color: "default", label: "skipped" };
  }
  return matches
    ? { color: "success", label: "ok" }
    : { color: "warning", label: "mismatch" };
};

const STATE_COLORS: Record<string, ChipColor> = {
  completed: "success",
  failed: "error",
  active: "info",
  waiting: "default",
  delayed: "default",
};

/** Props for {@link ConfigMigrationReport}. */
export type ConfigMigrationReportProps = {
  /** The current job status, or null when no run has started. */
  readonly status: ConfigMigrationStatusValue;
};

/**
 * Renders the outcome of a migration run: the job state, a dry-run marker, the
 * stored bundle keys, and a per-table count comparison. Shows a running notice
 * until the report is available and an error alert on failure.
 */
const ConfigMigrationReport = ({ status }: ConfigMigrationReportProps) => {
  if (status === null || status === undefined) {
    return null;
  }

  const { state, report } = status;
  const stateColor = STATE_COLORS[state] ?? "default";

  return (
    <Box sx={styles.root}>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={styles.header}
      >
        <Typography variant="subtitle1">Result</Typography>
        <Chip color={stateColor} label={state} size="small" />
        {report?.dryRun === true ? (
          <Chip label="dry run" size="small" variant="outlined" />
        ) : null}
      </Stack>

      {state === "failed" ? (
        <Alert severity="error">
          The migration job failed. Check the worker logs for the reason.
        </Alert>
      ) : null}

      {report === null || report === undefined ? (
        state === "failed" ? null : (
          <Typography color="text.secondary" variant="body2">
            Running…
          </Typography>
        )
      ) : (
        <>
          <Alert severity={report.allMatch ? "success" : "warning"}>
            {report.allMatch
              ? "All selected tables migrated as expected."
              : "Some tables did not match the source counts. Review below."}
          </Alert>

          <Box sx={styles.bundles}>
            <Typography variant="body2" color="text.secondary">
              Source bundle
            </Typography>
            <Typography sx={styles.mono}>{report.sourceBundleKey}</Typography>
            {report.preImageBundleKey !== null &&
            report.preImageBundleKey !== undefined ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  Target pre-image (backup for manual revert)
                </Typography>
                <Typography sx={styles.mono}>
                  {report.preImageBundleKey}
                </Typography>
              </>
            ) : null}
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Table</TableCell>
                <TableCell align="right">Source</TableCell>
                <TableCell align="right">Inserted + updated</TableCell>
                <TableCell align="right">Match</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {report.tables.map((table) => {
                const skipped =
                  table.skipReason !== null && table.skipReason !== undefined;
                const chip = matchChip(table.matches, skipped);
                return (
                  <TableRow key={table.tableName}>
                    <TableCell>
                      {table.tableName}
                      {skipped ? (
                        <Typography
                          color="text.secondary"
                          sx={styles.reason}
                          variant="caption"
                        >
                          {table.skipReason}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell align="right">{table.sourceCount}</TableCell>
                    <TableCell align="right">{table.targetCount}</TableCell>
                    <TableCell align="right">
                      <Chip
                        color={chip.color}
                        label={chip.label}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </>
      )}
    </Box>
  );
};

export default ConfigMigrationReport;
